import { ConnectionPositionPair } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  TellentAssistantConfigService,
  TellentAssistantFacade,
  TellentAssistantThreadsFacade,
} from '@core/tellent-assistant-logic';
import {
  TellentAssistantEmptyStateComponent,
  TellentAssistantHeaderComponent,
  TellentAssistantInputComponent,
  TellentAssistantMessageComponent,
  TellentAssistantThreadDrawerComponent,
  TellentAssistantThinkingIndicatorComponent,
} from '@core/tellent-assistant-ui';
import { AppKind, ORG_GUID } from '@core/tellent-types';
import { injectTellentAppUrlBuilder } from '@core/tellent-util';
import {
  ConnectedOverlayDirective,
  FIXED_OVERLAY_LEVEL,
  OverlayOriginDirective,
  WindowRef,
} from '@core/user-interface-legacy';

@Component({
  selector: 'tellent-assistant-chat-panel',
  standalone: true,
  imports: [
    TellentAssistantHeaderComponent,
    TellentAssistantEmptyStateComponent,
    TellentAssistantInputComponent,
    TellentAssistantThreadDrawerComponent,
    TellentAssistantMessageComponent,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    TellentAssistantThinkingIndicatorComponent,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tellent-assistant-chat-panel.component.html',
  styleUrls: ['./tellent-assistant-chat-panel.component.less'],
})
export class TellentAssistantChatPanelComponent {
  private readonly i18n = inject(I18nService);
  private readonly windowRef = inject(WindowRef);
  private readonly orgGuid = inject(ORG_GUID, { optional: true });
  private readonly urlBuilder = injectTellentAppUrlBuilder();
  private readonly drawerOverlay = viewChild<ConnectedOverlayDirective>('drawerOverlay');

  protected readonly facade = inject(TellentAssistantFacade);
  protected readonly threadsFacade = inject(TellentAssistantThreadsFacade);
  protected readonly config = inject(TellentAssistantConfigService);
  protected readonly drawerOpen = signal<boolean>(false);
  protected readonly title = this.i18n.translate('tlc.tellent_assistant.chat_panel.title');
  protected readonly drawerPositions = [
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' },
  ] satisfies ConnectionPositionPair[];

  public readonly close = output<void>();
  public readonly isDeleting = (id: string): boolean => this.threadsFacade.isThreadDeleting(id);

  public deleteThread(id: string): void {
    this.threadsFacade.deleteThread(id);
  }

  protected onDrawerAttach(): void {
    const host = this.drawerOverlay()?.overlayRef?.hostElement;
    if (host) host.style.zIndex = `${FIXED_OVERLAY_LEVEL + 1}`;
  }

  protected send(text: string): void {
    this.facade.send(text);
  }

  protected newThread(): void {
    this.facade.newThread();
  }

  protected selectThread(id: string): void {
    this.facade.switchThread(id);
  }

  protected openHelpCenter(): void {
    const url = this.i18n.translate('app.support_fab.help_center_settings_url');
    this.windowRef.nativeWindow.open(url, '_blank');
  }

  protected openIntelligenceSettings(): void {
    if (!this.orgGuid) return;
    const url = this.urlBuilder(AppKind.TellentAdmin, { id: 'intelligence' }, this.orgGuid);
    this.windowRef.nativeWindow.location = url;
  }
}
